// 2026-07-25:Media list hook。
//
// 状态机:loading / ready / error
// - loading:初始 fetch 中
// - ready:成功,持有 items + pagination
// - error:请求失败,持有 ApiError code/message,可 retry
//
// tenant 隔离:tenant_id 不传,后端从 g.current_user 读。
// 不在 API 失败时回退到 mock。

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../../api/client";
import {
  listMedia,
  type ListMediaParams,
  type MediaItem,
  type MediaListResult,
  type MediaPagination,
  type SyncMeta,
} from "../api/mediaApi";

export type LoadState =
  | { status: "loading" }
  | {
      status: "ready";
      items: MediaItem[];
      pagination: MediaPagination;
      // 2026-07-25:全量 Library 统计(不受 category / search 影响)
      libraryStats: MediaListResult["library_stats"];
      syncMeta: SyncMeta | null;
    }
  | { status: "error"; code: string; message: string };

export interface UseMediaListResult {
  state: LoadState;
  params: ListMediaParams;
  setParams: (next: ListMediaParams) => void;
  refresh: () => void;
}

export function useMediaList(initial: ListMediaParams = {}): UseMediaListResult {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [params, setParamsState] = useState<ListMediaParams>(initial);

  // 防 stale fetch:每次请求发一个 token,只接受最后一次的响应
  const tokenRef = useRef(0);

  const fetchOnce = useCallback(async (p: ListMediaParams) => {
    const token = ++tokenRef.current;
    setState({ status: "loading" });
    try {
      const result = await listMedia(p);
      if (token !== tokenRef.current) return; // 已被新的 fetch 取代
      setState({
        status: "ready",
        items: result.items ?? [],
        pagination: result.pagination ?? {
          page: 1,
          per_page: 24,
          total: 0,
          total_pages: 0,
        },
        libraryStats: result.library_stats ?? null,
        syncMeta: result.sync_meta ?? null,
      });
    } catch (e) {
      if (token !== tokenRef.current) return;
      const code = e instanceof ApiError ? e.code : "UNKNOWN";
      const message = e instanceof Error ? e.message : String(e);
      setState({ status: "error", code, message });
    }
  }, []);

  // params 变化或初次 mount → fetch
  useEffect(() => {
    fetchOnce(params);
  }, [fetchOnce, params]);

  const setParams = useCallback((next: ListMediaParams) => {
    setParamsState(next);
  }, []);

  const refresh = useCallback(() => {
    fetchOnce(params);
  }, [fetchOnce, params]);

  return { state, params, setParams, refresh };
}
